import React, { useCallback, useEffect, useRef, useState } from "react";
import flat from "flat";
import {
  ActionIcon,
  Anchor,
  Button,
  Loader,
  Modal,
  Progress,
  Text,
} from "@mantine/core";
import { BeatLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import { Trash } from "tabler-icons-react";
import { loadingStates } from "../../utilities/utilities";
import Editor from "../../components/Editor";
import TemplatesList from "../../components/TemplateManager/TemplatesList";
import { templatesDetailsApi } from "../../utilities/apis/templates";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import {
  createEditorField,
  getDocsToken,
  uploadFromLocal,
} from "../../utilities/apis/agreements";
import constants from "../../utilities/constants";

const notify = (title, message, color) =>
  showNotification({ title, message, color });

const buildEditorFormData = async (editorRef) => {
  const editor = editorRef.current.getDocumentEditor();
  const blob = await editor.saveAsBlob("Docx");
  const formData = new FormData();
  formData.append("file", blob, "MyDocument.docx");
  return formData;
};

const buildFileFormData = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
};

const normKey = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");

const mapPlaceholderValues = (fieldNames, rawValues = {}) => {
  const mapped = { ...rawValues };
  for (const name of fieldNames) {
    if (mapped[name]) continue;
    const hit = Object.entries(rawValues).find(
      ([k]) => normKey(k) === normKey(name),
    );
    if (hit) mapped[name] = hit[1];
  }
  return mapped;
};

const AgreementForm4 = ({ id, form, setUiConfigs, uiConfigs, save }) => {
  const navigate = useNavigate();
  const editorRef = useRef();
  const restoredTemplateRef = useRef(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [retainedFile, setRetainedFile] = useState(null);
  const [parentFilesLoaded, setParentFilesLoaded] = useState(!id);
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [sfdtContent, setSfdtContent] = useState(null);
  const [editorConfigs, setEditorConfigs] = useState({
    templateView: false,
    templateId: null,
    templateName: null,
    editorContent: { content: "agreement content goes here..." },
  });

  const showAlternateSection = selectedFile === null;
  const isEdit = Boolean(id);
  const hasNewFile = Boolean(selectedFile);
  const hasRetainedFile = Boolean(retainedFile);
  const hasTemplate = Boolean(editorConfigs.templateId);

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const saveAgreementInputField = useCallback(
    async (fieldNames) => {
      await apiWrapWithErrorWithData(
        createEditorField({
          fieldName: [...new Set(fieldNames)],
          agreementId: id,
        }),
      );
    },
    [id],
  );

  const loadTemplate = useCallback(async () => {
    const { templateId } = editorConfigs;
    if (!templateId) return;

    setTemplateLoading(true);
    try {
      const templateRes = await apiWrapWithErrorWithData(
        templatesDetailsApi({ templateId }),
      );
      if (!templateRes?.success || !templateRes?.template) {
        notify("Template", "Could not load the selected template", "red");
        return;
      }

      const { template } = templateRes;
      const fieldNames = template.fieldName ?? [];
      setPlaceholders(fieldNames);
      setPlaceholderValues((prev) => mapPlaceholderValues(fieldNames, prev));

      const tokenRes = await apiWrapWithErrorWithData(getDocsToken());
      const graphRes = await fetch(
        `https://graph.microsoft.com/v1.0/users/${constants.userId}/drive/items/${template.quillDelta}/content`,
        { headers: { Authorization: `Bearer ${tokenRes.data}` } },
      );

      if (graphRes.headers.get("Content-Length")) {
        const blob = await graphRes.blob();
        const file = new File([blob], "doc.docx", {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        const formData = new FormData();
        formData.append("files", file);
        const sfdtRes = await fetch(
          `${constants.wordeditorApi}/api/documenteditor/Import`,
          { method: "POST", body: formData },
        );
        setSfdtContent(await sfdtRes.json());
      }

      await saveAgreementInputField(fieldNames);
    } catch {
      notify("Template", "Could not load the selected template", "red");
    } finally {
      setTemplateLoading(false);
    }
  }, [editorConfigs.templateId, saveAgreementInputField]);

  useEffect(() => {
    if (restoredTemplateRef.current) return;
    const saved = flat.unflatten(form.values || {}).templateFields;
    if (!saved?.templateId) return;

    restoredTemplateRef.current = true;
    const rawValues = saved.values || {};
    const fieldNames =
      Array.isArray(saved.placeholders) && saved.placeholders.length
        ? saved.placeholders
        : Object.keys(rawValues);

    const mapped = mapPlaceholderValues(fieldNames, rawValues);

    setPlaceholderValues(mapped);
    setPlaceholders(fieldNames);
    setEditorConfigs((prev) => ({
      ...prev,
      templateId: saved.templateId,
      templateName: saved.templateName || null,
    }));
  }, [form.values]);

  useEffect(() => {
    if (editorConfigs.templateId) loadTemplate();
  }, [editorConfigs.templateId, loadTemplate]);

  const savePrev = () => setUiConfigs({ ...uiConfigs, currentStep: 3 });

  const saveNext = async () => {
    const usingEditorPath = showAlternateSection;
    const canSkipUpload =
      isEdit && hasRetainedFile && !hasNewFile && !hasTemplate;

    if (!isEdit && !hasNewFile && !usingEditorPath) {
      notify(
        "Document required",
        "Please upload a .docx file or choose a template.",
        "red",
      );
      return;
    }

    if (isEdit && !hasNewFile && !hasRetainedFile && !usingEditorPath) {
      notify(
        "Document required",
        "No agreement document is available to save.",
        "red",
      );
      return;
    }

    setLoading(true);

    try {
      const editorValues =
        editorRef.current?.getPlaceholderValues?.() || placeholderValues || {};
      const nextValues = mapPlaceholderValues(placeholders, editorValues);
      setPlaceholderValues(nextValues);
      editorRef.current?.applyPlaceholderValues?.(nextValues);

      const templateFields = editorConfigs.templateId
        ? {
            templateId: editorConfigs.templateId,
            templateName: editorConfigs.templateName,
            placeholders,
            values: nextValues,
          }
        : undefined;

      if (templateFields) {
        form.setFieldValue("templateFields", templateFields);
      }

      const saved = await save({
        ...form.values,
        formCompleted: true,
        ...(templateFields ? { templateFields } : {}),
      });

      if (saved === false) {
        notify("Save failed", "Could not save placeholder values", "red");
        return;
      }

      if (!canSkipUpload) {
        const formData = selectedFile
          ? buildFileFormData(selectedFile)
          : await buildEditorFormData(editorRef);

        await uploadFromLocal({ agreementId: id, formData });
        notify("Upload successful", "Document uploaded successfully", "green");
      } else {
        notify(
          "Saved",
          "Agreement updated without replacing the document.",
          "green",
        );
      }
      navigate(`/app/agreements_manager/agreements/details/${id}`);
    } catch {
      notify("Upload failed", "Could not upload the document", "red");
    } finally {
      setLoading(false);
    }
  };

  const busy =
    uiConfigs.loading === loadingStates.LOADING || loading || templateLoading;

  return (
    <>
      <div className="my-4 w-full max-w-10/12 px-8">
        <div className="mt-4 w-full">
          {selectedFile ? (
            <SelectedFileRow file={selectedFile} onRemove={handleRemoveFile} />
          ) : (
            <FileDropzone onFileSelect={setSelectedFile} />
          )}
        </div>
      </div>

      {showAlternateSection && (
        <>
          <OrDivider />

          {editorConfigs.templateView && (
            <Modal
              overflow="inside"
              opened
              onClose={() =>
                setEditorConfigs((prev) => ({ ...prev, templateView: false }))
              }
              size="calc(80vw)"
              title="Choose a template"
              padding="lg"
            >
              <Text className="mb-4 text-sm text-gray-500">
                Preview a template or click Use to apply it directly to your
                agreement.
              </Text>
              <TemplatesList
                pickerMode
                showNewTemplateBtn={false}
                templateType="Agreements"
                placeholders={placeholders}
                setPlaceholders={setPlaceholders}
                useTemplateFunc={(template) => {
                  setTemplateLoading(true);
                  setSfdtContent(null);
                  setPlaceholders([]);
                  setPlaceholderValues({});
                  setEditorConfigs((prev) => ({
                    ...prev,
                    templateView: false,
                    editorContent: template.quillDelta,
                    templateId: template.id,
                    templateName: template.name,
                  }));
                }}
              />
            </Modal>
          )}

          <div className="flex w-full flex-col items-center px-8">
            <div className="mb-3 flex w-full items-center justify-between gap-3">
              <Button
                className="bg-[#46BDE1] hover:bg-[#3aa8c9]"
                onClick={() =>
                  setEditorConfigs((prev) => ({ ...prev, templateView: true }))
                }
                disabled={templateLoading}
              >
                Choose Template
              </Button>

              {editorConfigs.templateName && (
                <span className="inline-flex items-center gap-2 rounded-full border border-[#46BDE1] bg-[#ecf9fd] px-3 py-1.5 text-[13px] text-teal-700">
                  Using: {editorConfigs.templateName}
                </span>
              )}
            </div>

            <div className="relative min-h-[400px] w-full overflow-hidden bg-white">
              {templateLoading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90">
                  <Loader color="#46BDE1" size="lg" />
                  <Text mt="md" color="dimmed">
                    Loading template...
                  </Text>
                </div>
              )}
              <Editor
                key={editorConfigs.templateId || "no-template"}
                placeholders={placeholders}
                setPlaceholders={setPlaceholders}
                initialValues={placeholderValues}
                onValuesChange={setPlaceholderValues}
                disabled
                ref={editorRef}
                initialContent={sfdtContent}
                initialValues={placeholderValues}
                editNotAllowed={!!editorConfigs.templateId}
              />
            </div>
          </div>
        </>
      )}

      <div className="mt-10 flex items-center justify-between">
        <Button
          className="bg-[#46BDE1] hover:bg-[#3aa8c9]"
          onClick={savePrev}
          disabled={busy}
        >
          Previous
        </Button>
        <Button
          className="bg-[#46BDE1] hover:bg-[#3aa8c9]"
          onClick={saveNext}
          disabled={busy}
        >
          {busy ? <BeatLoader color="#fff" size={10} /> : "Submit"}
        </Button>
      </div>
    </>
  );
};

export default AgreementForm4;

const FileDropzone = ({ onFileSelect }) => (
  <div className="m-4 flex items-center justify-center">
    <label
      htmlFor="dropzone-file"
      className="flex h-34 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100"
    >
      <div className="flex flex-col items-center justify-center pb-6 pt-5">
        <UploadIcon />
        <p className="mb-2 text-sm text-gray-500">
          <span>Click to upload</span> or drag and drop
        </p>
        <p className="text-xs text-gray-500">Only .docx files are supported</p>
      </div>
      <input
        id="dropzone-file"
        type="file"
        accept=".docx"
        className="hidden"
        onChange={(e) => onFileSelect(e.target.files[0])}
      />
    </label>
  </div>
);

const SelectedFileRow = ({ file, onRemove }) => (
  <div className="mx-6 flex w-full items-center justify-center">
    <div className="mt-4 flex w-full flex-row">
      <img src="/assets/images/downloadFileLogo.svg" alt="file upload" />
      <div className="flex w-10/12 flex-col pl-3">
        <div className="text-xl">{file.name}</div>
        <div className="mt-3">
          <Progress color="teal" size="sm" value={100} />
        </div>
      </div>
      <div className="flex items-center">
        <Anchor className="mx-3" color="#46BDE1">
          <Text>{file.fileName}</Text>
        </Anchor>
        <ActionIcon color="red" onClick={onRemove} aria-label="Remove file">
          <Trash />
        </ActionIcon>
      </div>
    </div>
  </div>
);

const OrDivider = () => (
  <div className="flex w-full items-center px-8">
    <hr className="w-[45%] border-t-[3px] border-dotted border-black" />
    <Text className="my-4 w-[10%] text-center text-xl font-bold text-[#46BDE1]">
      OR
    </Text>
    <hr className="w-[45%] border-t-[3px] border-dotted border-black" />
  </div>
);

const UploadIcon = () => (
  <svg
    className="mb-4 h-8 w-8 text-gray-500"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 20 16"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
    />
  </svg>
);
